import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { packageMeta } from "@/lib/packages";
import { ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Bento layout patterns (6 slots) — vary per load for extra editorial feel
const LAYOUT_PATTERNS = [
  [
    { className: "md:col-span-8 md:row-span-2", height: "480px", size: "lg" },
    { className: "md:col-span-4", height: "230px", size: "sm" },
    { className: "md:col-span-4", height: "230px", size: "sm" },
    { className: "md:col-span-6", height: "260px", size: "md" },
    { className: "md:col-span-6", height: "260px", size: "md" },
    { className: "md:col-span-12", height: "300px", size: "lg" },
  ],
  [
    { className: "md:col-span-6", height: "300px", size: "md" },
    { className: "md:col-span-6", height: "300px", size: "md" },
    { className: "md:col-span-8 md:row-span-2", height: "480px", size: "lg" },
    { className: "md:col-span-4", height: "230px", size: "sm" },
    { className: "md:col-span-4", height: "230px", size: "sm" },
    { className: "md:col-span-12", height: "260px", size: "lg" },
  ],
  [
    { className: "md:col-span-4", height: "260px", size: "sm" },
    { className: "md:col-span-8 md:row-span-2", height: "540px", size: "lg" },
    { className: "md:col-span-4", height: "260px", size: "sm" },
    { className: "md:col-span-6", height: "260px", size: "md" },
    { className: "md:col-span-6", height: "260px", size: "md" },
    { className: "md:col-span-12", height: "280px", size: "lg" },
  ],
];

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const Card = ({ pkg, meta, layout, overline, from, per_person, index }) => (
  <Link
    to={`/tours/${pkg.id}`}
    data-testid={`dest-card-${pkg.id}`}
    className={`relative overflow-hidden border border-jungle group cursor-pointer fade-up ${layout.className}`}
    style={{ minHeight: layout.height, animationDelay: `${index * 60}ms` }}
  >
    <img
      src={meta.image}
      alt={pkg.name}
      loading="lazy"
      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-110"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-jungle/95 via-jungle/40 to-transparent" />

    <div className="absolute top-4 right-4 w-10 h-10 md:w-11 md:h-11 border border-sand/40 flex items-center justify-center rounded-full text-sand backdrop-blur-sm bg-jungle/20 group-hover:bg-terracotta group-hover:border-terracotta group-hover:-rotate-12 transition-all duration-500">
      <ArrowUpRight size={layout.size === "sm" ? 16 : 18} />
    </div>

    <div className="relative h-full flex flex-col justify-end p-6 md:p-8 text-white">
      <div className="font-overline text-[10px] text-terracotta">
        {from} <span className="text-white font-display normal-case tracking-normal text-base ml-1">${pkg.full_price}</span>
        <span className="text-sand/60 ml-1">{per_person}</span>
      </div>
      <h3 className={`font-display mt-2 ${layout.size === "lg" ? "text-3xl md:text-4xl" : layout.size === "md" ? "text-2xl md:text-3xl" : "text-xl md:text-2xl"}`}>
        {pkg.name}
      </h3>
      <div className="text-sand/70 text-xs md:text-sm mt-1 font-overline">
        {pkg.location}
      </div>
    </div>
  </Link>
);

export default function Destinations() {
  const { t } = useLang();
  const [packages, setPackages] = useState([]);

//  useEffect(() => {
//    axios.get(`${API}/packages`).then(r => setPackages(r.data)).catch(() => {});
//  }, []);

   useEffect(() => {
    axios.get(`${API}/packages`, {
      params: {
        show_destination:true // ou false, ou uma variável do seu estado
      }
    })
    .then(r => setPackages(r.data))
    .catch(() => {});
  }, []); // Se usar uma variável de estado aqui, lembre-se de colocá-la no array de dependências


  // Shuffle both the packages AND pick a random layout pattern per load
  const { ordered, layout } = useMemo(() => {
    if (!packages.length) return { ordered: [], layout: LAYOUT_PATTERNS[0] };
    return {
      ordered: shuffle(packages),
      layout: LAYOUT_PATTERNS[Math.floor(Math.random() * LAYOUT_PATTERNS.length)],
    };
  }, [packages]);

  return (
    <section id="destinations" data-testid="destinations-section" className="py-14 md:py-4 bg-sand">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl mb-12 md:mb-16 flex items-end justify-between">
          <div>
            <div className="font-overline text-terracotta text-xs mb-3">{t.destinations.overline}</div>
            <h3 className="font-display text-2xl md:text-2xl text-jungle leading-[1.05]">{t.destinations.title}</h3>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          {ordered.slice(0, 5).map((pkg, i) => {
            const meta = packageMeta[pkg.id] || {};
            const slot = layout[i] || layout[layout.length - 1];
            return (
              <Card
                key={pkg.id}
                pkg={pkg}
                meta={meta}
                layout={slot}
                index={i}
                overline={pkg.location}
                from={t.destinations.from}
                per_person={t.destinations.per_person}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}
